import axios from 'axios';
import { SupplierServiceBillCreateDTO } from '../interfaces/procurement/supplierBill.interface';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import {
    EquipmentDTO, EquipmentFilters, EquipmentGroupDTO, EquipmentGroupRequest, EquipmentImportResult, EquipmentMoveDTO, EquipmentRequest,
    EquipmentStatus, EquipmentTypeDTO, EquipmentTypeRequest, FailureCodeDTO, FailureCodeRequest,
    MaintenanceSettings, MeterDTO, MeterRequest, MntPage, SeedResult,
    LogSheet, MeterReadingBatchResult, MeterReadingDTO, MeterReadingItem, MeterSummary,
    BreakdownRequestDTO, EquipmentLookupDTO, ExternalRepairRequest, LabourDTO, LabourRequest, LocationLookupDTO,
    MyBreakdownRequestView, RaiseBreakdownRequest, TechnicianDTO, WoRequisitionView, WorkOrderCloseRequest,
    WorkOrderCompleteRequest, WorkOrderCreateRequest, WorkOrderDetailDTO, WorkOrderDTO, WorkOrderFilters,
    ChecklistItemDTO, ChecklistResult,
    MntManagerDashboard, MntTechnicianDashboard,
    RepairBillDTO, RepairBillFile, RepairBillSaveRequest, RepairBillStatus,
    PmBatchEditRequest, PmBulkRequest, PmBulkResult, PmGenerationResult, PmPlanCreateRequest, PmPlanDTO, PmPlanUpdateRequest,
} from '../interfaces/maintenance';

const headers = () => AuthService.getAuthHeader();
const M = ENDPOINTS.MAINTENANCE;

/** Drop empty filter values so they are not sent as `q=&typeId=`. */
const clean = (o: Record<string, unknown>) =>
    Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && v !== ''));

export const MaintenanceService = {
    listEquipment: async (institutionId: string, filters: EquipmentFilters, page = 0, size = 20): Promise<MntPage<EquipmentDTO>> =>
        (await axios.get(M.EQUIPMENT_LIST(institutionId), { headers: headers(), params: clean({ ...filters, page, size }) })).data,
    getEquipment: async (id: string): Promise<EquipmentDTO> =>
        (await axios.get(M.EQUIPMENT_BY_ID(id), { headers: headers() })).data,
    createEquipment: async (req: EquipmentRequest): Promise<EquipmentDTO> =>
        (await axios.post(M.EQUIPMENT, req, { headers: headers() })).data,
    updateEquipment: async (id: string, req: EquipmentRequest): Promise<EquipmentDTO> =>
        (await axios.put(M.EQUIPMENT_BY_ID(id), req, { headers: headers() })).data,
    deleteEquipment: async (id: string): Promise<void> => {
        await axios.delete(M.EQUIPMENT_BY_ID(id), { headers: headers() });
    },
    listMoves: async (id: string): Promise<EquipmentMoveDTO[]> =>
        (await axios.get(M.EQUIPMENT_MOVES(id), { headers: headers() })).data,
    moveEquipment: async (id: string, toDepartmentId: string, reason: string): Promise<EquipmentDTO> =>
        (await axios.post(M.EQUIPMENT_MOVE(id), { toDepartmentId, reason }, { headers: headers() })).data,
    setStatus: async (id: string, status: EquipmentStatus, reason: string): Promise<EquipmentDTO> =>
        (await axios.post(M.EQUIPMENT_STATUS(id), { status, reason }, { headers: headers() })).data,

    addMeter: async (equipmentId: string, req: MeterRequest): Promise<MeterDTO> =>
        (await axios.post(M.EQUIPMENT_METERS(equipmentId), req, { headers: headers() })).data,
    updateMeter: async (meterId: string, req: MeterRequest): Promise<MeterDTO> =>
        (await axios.put(M.METER_BY_ID(meterId), req, { headers: headers() })).data,
    deleteMeter: async (meterId: string): Promise<void> => {
        await axios.delete(M.METER_BY_ID(meterId), { headers: headers() });
    },

    getLogSheet: async (institutionId: string, params: Record<string, unknown>): Promise<LogSheet> =>
        (await axios.get(M.LOG_SHEET(institutionId), { headers: headers(), params: clean(params) })).data,
    recordReadings: async (institutionId: string, readings: MeterReadingItem[]): Promise<MeterReadingBatchResult> =>
        (await axios.post(M.READINGS_BATCH(institutionId), { readings }, { headers: headers() })).data,
    getMeterSummary: async (meterId: string): Promise<MeterSummary> =>
        (await axios.get(M.METER_SUMMARY(meterId), { headers: headers() })).data,
    listReadings: async (meterId: string, from: string, to: string, page = 0, size = 50): Promise<MntPage<MeterReadingDTO>> =>
        (await axios.get(M.METER_READINGS(meterId), { headers: headers(), params: clean({ from, to, page, size }) })).data,
    correctReading: async (readingId: string, value: number, isReset: boolean, note: string): Promise<MeterReadingDTO> =>
        (await axios.post(M.READING_CORRECT(readingId), { value, isReset, note }, { headers: headers() })).data,

    importEquipment: async (institutionId: string, file: File, dryRun: boolean): Promise<EquipmentImportResult> => {
        const form = new FormData();
        form.append('file', file);
        return (await axios.post(M.EQUIPMENT_IMPORT(institutionId), form, { headers: headers(), params: { dryRun } })).data;
    },
    exportEquipmentCsv: async (institutionId: string): Promise<Blob> =>
        (await axios.get(M.EQUIPMENT_EXPORT(institutionId), { headers: headers(), responseType: 'blob' })).data,

    listTypes: async (institutionId: string): Promise<EquipmentTypeDTO[]> =>
        (await axios.get(M.TYPE_LIST(institutionId), { headers: headers() })).data,
    createType: async (req: EquipmentTypeRequest): Promise<EquipmentTypeDTO> =>
        (await axios.post(M.TYPES, req, { headers: headers() })).data,
    updateType: async (id: string, req: EquipmentTypeRequest): Promise<EquipmentTypeDTO> =>
        (await axios.put(M.TYPE_BY_ID(id), req, { headers: headers() })).data,
    deleteType: async (id: string): Promise<void> => {
        await axios.delete(M.TYPE_BY_ID(id), { headers: headers() });
    },

    listGroups: async (institutionId: string): Promise<EquipmentGroupDTO[]> =>
        (await axios.get(M.GROUP_LIST(institutionId), { headers: headers() })).data,
    createGroup: async (req: EquipmentGroupRequest): Promise<EquipmentGroupDTO> =>
        (await axios.post(M.GROUPS, req, { headers: headers() })).data,
    updateGroup: async (id: string, req: EquipmentGroupRequest): Promise<EquipmentGroupDTO> =>
        (await axios.put(M.GROUP_BY_ID(id), req, { headers: headers() })).data,
    setGroupActive: async (id: string, active: boolean): Promise<EquipmentGroupDTO> =>
        (await axios.put(active ? M.GROUP_ACTIVATE(id) : M.GROUP_DEACTIVATE(id), {}, { headers: headers() })).data,
    deleteGroup: async (id: string): Promise<void> => {
        await axios.delete(M.GROUP_BY_ID(id), { headers: headers() });
    },

    getSettings: async (institutionId: string): Promise<MaintenanceSettings> =>
        (await axios.get(M.SETTINGS(institutionId), { headers: headers() })).data,
    saveSettings: async (institutionId: string, req: Omit<MaintenanceSettings, 'institutionId'>): Promise<MaintenanceSettings> =>
        (await axios.put(M.SETTINGS(institutionId), req, { headers: headers() })).data,
    seedDefaults: async (institutionId: string): Promise<SeedResult> =>
        (await axios.post(M.SEED_DEFAULTS(institutionId), {}, { headers: headers() })).data,

    listFailureCodes: async (institutionId: string): Promise<FailureCodeDTO[]> =>
        (await axios.get(M.FAILURE_CODE_LIST(institutionId), { headers: headers() })).data,
    createFailureCode: async (req: FailureCodeRequest): Promise<FailureCodeDTO> =>
        (await axios.post(M.FAILURE_CODES, req, { headers: headers() })).data,
    updateFailureCode: async (id: string, req: FailureCodeRequest): Promise<FailureCodeDTO> =>
        (await axios.put(M.FAILURE_CODE_BY_ID(id), req, { headers: headers() })).data,
    deleteFailureCode: async (id: string): Promise<void> => {
        await axios.delete(M.FAILURE_CODE_BY_ID(id), { headers: headers() });
    },

    // ------------------------------------------------------------ L1: work orders
    listWorkOrders: async (institutionId: string, filters: WorkOrderFilters, page = 0, size = 20): Promise<MntPage<WorkOrderDTO>> =>
        (await axios.get(M.WO_LIST(institutionId), { headers: headers(), params: clean({ ...filters, page, size }) })).data,
    listMyWorkOrders: async (institutionId: string, status?: string, page = 0, size = 50): Promise<MntPage<WorkOrderDTO>> =>
        (await axios.get(M.WO_MINE(institutionId), { headers: headers(), params: clean({ status, page, size }) })).data,
    listTechnicians: async (institutionId: string): Promise<TechnicianDTO[]> =>
        (await axios.get(M.WO_TECHNICIANS(institutionId), { headers: headers() })).data,
    getWorkOrder: async (id: string): Promise<WorkOrderDetailDTO> =>
        (await axios.get(M.WO_BY_ID(id), { headers: headers() })).data,
    createWorkOrder: async (req: WorkOrderCreateRequest): Promise<WorkOrderDTO> =>
        (await axios.post(M.WO, req, { headers: headers() })).data,
    workOrderAction: async (id: string, action: 'assign' | 'start' | 'hold' | 'resume' | 'reopen' | 'cancel',
        body: Record<string, unknown> = {}, version?: number): Promise<WorkOrderDTO> =>
        (await axios.post(M.WO_ACTION(id, action), body, { headers: headers(), params: clean({ version }) })).data,
    restoreWorkOrderFromArchive: async (id: string): Promise<WorkOrderDTO> =>
        (await axios.post(M.WO_ACTION(id, 'restore-from-archive'), {}, { headers: headers() })).data,
    completeWorkOrder: async (id: string, req: WorkOrderCompleteRequest, version?: number): Promise<WorkOrderDTO> =>
        (await axios.post(M.WO_ACTION(id, 'complete'), req, { headers: headers(), params: clean({ version }) })).data,
    closeWorkOrder: async (id: string, req: WorkOrderCloseRequest, version?: number): Promise<WorkOrderDTO> =>
        (await axios.post(M.WO_ACTION(id, 'close'), req, { headers: headers(), params: clean({ version }) })).data,
    addLabour: async (id: string, req: LabourRequest): Promise<LabourDTO> =>
        (await axios.post(M.WO_LABOUR(id), req, { headers: headers() })).data,
    deleteLabour: async (id: string, labourId: string): Promise<void> => {
        await axios.delete(M.WO_LABOUR_BY_ID(id, labourId), { headers: headers() });
    },
    addChecklistItem: async (id: string, task: string): Promise<ChecklistItemDTO> =>
        (await axios.post(M.WO_CHECKLIST(id), { task }, { headers: headers() })).data,
    setChecklistResult: async (id: string, itemId: string, result: ChecklistResult, remark?: string): Promise<ChecklistItemDTO> =>
        (await axios.put(M.WO_CHECKLIST_ITEM(id, itemId), { result, remark }, { headers: headers() })).data,
    requestParts: async (id: string, lines: { itemId: string; qty: number }[]): Promise<WoRequisitionView> =>
        (await axios.post(M.WO_PARTS(id), { lines }, { headers: headers() })).data,
    attachRequisition: async (id: string, requisitionRefNo: string): Promise<WoRequisitionView> =>
        (await axios.post(M.WO_PARTS_ATTACH(id), { requisitionRefNo }, { headers: headers() })).data,
    saveExternalRepair: async (id: string, req: ExternalRepairRequest, version?: number): Promise<WorkOrderDTO> =>
        (await axios.put(M.WO_EXTERNAL(id), req, { headers: headers(), params: clean({ version }) })).data,

    // ------------------------------------------------------------ L1: triage inbox
    listRequests: async (institutionId: string, status?: string, page = 0, size = 20): Promise<MntPage<BreakdownRequestDTO>> =>
        (await axios.get(M.REQUEST_LIST(institutionId), { headers: headers(), params: clean({ status, page, size }) })).data,
    getRequest: async (id: string): Promise<BreakdownRequestDTO> =>
        (await axios.get(M.REQUEST_BY_ID(id), { headers: headers() })).data,
    acceptRequest: async (id: string, body: { priority?: string; assignTo?: string; title?: string }): Promise<BreakdownRequestDTO> =>
        (await axios.post(M.REQUEST_ACTION(id, 'accept'), body, { headers: headers() })).data,
    rejectRequest: async (id: string, reason: string): Promise<BreakdownRequestDTO> =>
        (await axios.post(M.REQUEST_ACTION(id, 'reject'), { reason }, { headers: headers() })).data,
    mergeRequest: async (id: string, targetRequestId: string): Promise<BreakdownRequestDTO> =>
        (await axios.post(M.REQUEST_ACTION(id, 'merge'), { targetRequestId }, { headers: headers() })).data,

    // ------------------------------------------------------------ L3: preventive maintenance
    listPmPlans: async (institutionId: string, filters: { equipmentId?: string; typeId?: string; active?: boolean | '' }, page = 0, size = 20): Promise<MntPage<PmPlanDTO>> =>
        (await axios.get(M.PM_LIST(institutionId), { headers: headers(), params: clean({ ...filters, page, size }) })).data,
    listPmDue: async (institutionId: string, days = 7): Promise<PmPlanDTO[]> =>
        (await axios.get(M.PM_DUE(institutionId), { headers: headers(), params: { days } })).data,
    listPmForEquipment: async (institutionId: string, equipmentId: string): Promise<PmPlanDTO[]> =>
        (await axios.get(M.PM_FOR_EQUIPMENT(institutionId, equipmentId), { headers: headers() })).data,
    getPmPlan: async (id: string): Promise<PmPlanDTO> =>
        (await axios.get(M.PM_BY_ID(id), { headers: headers() })).data,
    createPmPlan: async (req: PmPlanCreateRequest): Promise<PmPlanDTO> =>
        (await axios.post(M.PM, req, { headers: headers() })).data,
    updatePmPlan: async (id: string, req: PmPlanUpdateRequest): Promise<PmPlanDTO> =>
        (await axios.put(M.PM_BY_ID(id), req, { headers: headers() })).data,
    bulkPmPlans: async (institutionId: string, req: PmBulkRequest): Promise<PmBulkResult> =>
        (await axios.post(M.PM_BULK(institutionId), req, { headers: headers() })).data,
    batchEditPmPlans: async (batchKey: string, req: PmBatchEditRequest): Promise<{ updated: number }> =>
        (await axios.put(M.PM_BATCH(batchKey), req, { headers: headers() })).data,
    generatePmNow: async (institutionId: string): Promise<PmGenerationResult> =>
        (await axios.post(M.PM_GENERATE_NOW(institutionId), {}, { headers: headers() })).data,
};

/** Self-service: any employee of the institution, no permission key. */
export const MyBreakdownService = {
    raise: async (req: RaiseBreakdownRequest): Promise<MyBreakdownRequestView> =>
        (await axios.post(M.MY_BREAKDOWNS, req, { headers: headers() })).data,
    listMine: async (institutionId: string, page = 0, size = 20): Promise<MntPage<MyBreakdownRequestView>> =>
        (await axios.get(M.MY_BREAKDOWN_LIST(institutionId), { headers: headers(), params: { page, size } })).data,
    getMine: async (id: string): Promise<MyBreakdownRequestView> =>
        (await axios.get(M.MY_BREAKDOWN_BY_ID(id), { headers: headers() })).data,
    cancelMine: async (id: string): Promise<MyBreakdownRequestView> =>
        (await axios.post(M.MY_BREAKDOWN_CANCEL(id), {}, { headers: headers() })).data,
    lookupEquipment: async (institutionId: string, q: string, equipmentId?: string): Promise<EquipmentLookupDTO[]> =>
        (await axios.get(M.MY_BREAKDOWN_EQUIPMENT(institutionId), { headers: headers(), params: clean({ q, equipmentId }) })).data,
    lookupLocations: async (institutionId: string): Promise<LocationLookupDTO[]> =>
        (await axios.get(M.MY_BREAKDOWN_LOCATIONS(institutionId), { headers: headers() })).data,
};

/** Maintenance L4 boards. Manager needs WO_VIEW_ALL; technician returns only the caller's own work. */
export const MaintenanceDashboardService = {
    manager: async (institutionId: string): Promise<MntManagerDashboard> =>
        (await axios.get(M.DASHBOARD_MANAGER(institutionId), { headers: headers() })).data,
    technician: async (institutionId: string): Promise<MntTechnicianDashboard> =>
        (await axios.get(M.DASHBOARD_TECHNICIAN(institutionId), { headers: headers() })).data,
};

/** Maintenance L5 reports. Filters passed in are the APPLIED ones; the CSV twin takes the same params (never page/size). */
export const MaintenanceReportService = {
    get: async <T = unknown>(institutionId: string, slug: string, params: Record<string, unknown>): Promise<T> =>
        (await axios.get(M.REPORT(institutionId, slug), { headers: headers(), params: clean(params) })).data,
    csv: async (institutionId: string, slug: string, params: Record<string, unknown>): Promise<Blob> =>
        (await axios.get(M.REPORT_CSV(institutionId, slug), { headers: headers(), params: clean(params), responseType: 'blob' })).data,
};

/** Repair bills: maintenance submits from a work order, accounts approves (SUPPLIER_BILL_CREATE) through the service-bill form. */
export const RepairBillService = {
    listForWorkOrder: async (woId: string): Promise<RepairBillDTO[]> =>
        (await axios.get(M.REPAIR_BILLS_OF_WO(woId), { headers: headers() })).data,
    create: async (woId: string, req: RepairBillSaveRequest): Promise<RepairBillDTO> =>
        (await axios.post(M.REPAIR_BILLS_OF_WO(woId), req, { headers: headers() })).data,
    update: async (id: string, req: RepairBillSaveRequest): Promise<RepairBillDTO> =>
        (await axios.put(M.REPAIR_BILL(id), req, { headers: headers() })).data,
    remove: async (id: string): Promise<void> => {
        await axios.delete(M.REPAIR_BILL(id), { headers: headers() });
    },
    submit: async (id: string): Promise<RepairBillDTO> =>
        (await axios.put(M.REPAIR_BILL_SUBMIT(id), {}, { headers: headers() })).data,
    get: async (id: string): Promise<RepairBillDTO> =>
        (await axios.get(M.REPAIR_BILL(id), { headers: headers() })).data,
    queue: async (institutionId: string, status?: RepairBillStatus): Promise<RepairBillDTO[]> =>
        (await axios.get(M.REPAIR_BILL_QUEUE(institutionId), { headers: headers(), params: status ? { status } : {} })).data,
    waitingCount: async (institutionId: string): Promise<number> =>
        (await axios.get(M.REPAIR_BILL_QUEUE_COUNT(institutionId), { headers: headers() })).data,
    /** Posts the service bill and approves the repair bill in ONE server transaction. */
    approveWithBill: async (id: string, bill: SupplierServiceBillCreateDTO): Promise<RepairBillDTO> =>
        (await axios.post(M.REPAIR_BILL_APPROVE_WITH_BILL(id), bill, { headers: headers() })).data,
    /** Links a service bill that is already posted. */
    linkExisting: async (id: string, supplierBillId: string): Promise<RepairBillDTO> =>
        (await axios.put(M.REPAIR_BILL_LINK(id), { supplierBillId }, { headers: headers() })).data,
    reject: async (id: string, reason: string): Promise<RepairBillDTO> =>
        (await axios.put(M.REPAIR_BILL_REJECT(id), { reason }, { headers: headers() })).data,
    listFiles: async (id: string): Promise<RepairBillFile[]> =>
        (await axios.get(M.REPAIR_BILL_FILES(id), { headers: headers() })).data,
    uploadFile: async (id: string, file: File): Promise<RepairBillFile> => {
        const form = new FormData();
        form.append('file', file);
        return (await axios.post(M.REPAIR_BILL_FILES(id), form, { headers: headers() })).data;
    },
    deleteFile: async (id: string, fileId: string): Promise<void> => {
        await axios.delete(M.REPAIR_BILL_FILE(id, fileId), { headers: headers() });
    },
    downloadFile: async (id: string, fileId: string): Promise<{ blob: Blob; fileName: string }> => {
        const response = await axios.get(M.REPAIR_BILL_FILE(id, fileId), { headers: headers(), responseType: 'blob' });
        const disposition: string = response.headers?.['content-disposition'] ?? '';
        const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
        const plain = /filename="?([^";]+)"?/i.exec(disposition);
        let fileName = plain ? plain[1] : 'repair-bill';
        if (star) {
            try { fileName = decodeURIComponent(star[1]); } catch { /* keep the plain name */ }
        }
        return { blob: response.data, fileName };
    },
};
